/**
 * The way around a twenty hour book: chapters, and full text search inside it.
 *
 * The panel is one slide over rather than two, because a chapter list and a
 * search result list answer the same question, and switching between two panels
 * to ask it twice is a tax on the reader.
 */

import { useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";

import { CloseIcon, SearchIcon } from "../components/icons";
import { IconButton, clock } from "../components/ui";
import { t, useUiLang } from "../i18n";
import { api } from "../lib/api";
import { useScrollLock } from "../lib/useScrollLock";
import type { BookDetail, PageMap, SearchHit } from "../lib/types";
import { pageAt } from "../lib/useReader";

/** Render a `<mark>` snippet without trusting it as HTML: the book text is not ours. */
function Snippet({ value }: { value: string }) {
  const parts = value.split(/<\/?mark>/);
  return (
    <span>
      {parts.map((part, at) =>
        at % 2 === 1 ? (
          <mark key={at} className="bg-transparent text-[var(--color-accent)]">
            {part}
          </mark>
        ) : (
          <span key={at}>{part}</span>
        ),
      )}
    </span>
  );
}

export function ChapterNav({
  open,
  book,
  pagemap,
  chapterIndex,
  searchRef,
  onClose,
  onGoToChapter,
  onGoToSentence,
}: {
  open: boolean;
  book: BookDetail;
  pagemap: PageMap | null;
  chapterIndex: number;
  searchRef: MutableRefObject<HTMLInputElement | null>;
  onClose: () => void;
  onGoToChapter: (index: number) => void;
  onGoToSentence: (index: number) => void;
}) {
  useUiLang();
  const [term, setTerm] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const list = useRef<HTMLDivElement>(null);

  // The reading surface behind must not scroll under the finger while the panel
  // is over it: on a phone the two scrollers fight and the reader loses their
  // place in the chapter they came from.
  useScrollLock(open);

  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) {
      setHits(null);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = window.setTimeout(() => {
      api
        .search(query, { book: book.id, limit: 40 }, controller.signal)
        .then((answer) => {
          setHits(answer.hits);
          setSearching(false);
        })
        .catch(() => undefined);
    }, 220);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [book.id, term]);

  // Bring the chapter being read into view when the panel opens.
  useEffect(() => {
    if (!open || hits) return;
    const current = list.current?.querySelector(`[data-chapter="${chapterIndex}"]`);
    current?.scrollIntoView({ block: "center" });
  }, [chapterIndex, hits, open]);

  const marks = useMemo(() => book.chapter_marks ?? [], [book.chapter_marks]);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={
          "absolute inset-0 z-20 touch-none overscroll-contain bg-black/50 transition-opacity duration-200 " +
          (open ? "opacity-100" : "pointer-events-none opacity-0")
        }
      />
      <aside
        aria-label={t("chapterNavLabel")}
        aria-hidden={!open}
        className={
          // Narrow screen: the panel is the screen bar a thumb's width of the
          // page behind, which is what tells the reader they can tap out of it.
          // It never gets wider than the 22rem it had on a laptop.
          "absolute inset-y-0 left-0 z-20 flex w-[calc(100%-2.5rem)] max-w-fib377 flex-col " +
          "bg-[var(--color-surface)] shadow-2xl shadow-black/60 " +
          "transition-[transform,visibility] duration-300 [transition-timing-function:var(--ease-out)] " +
          (open ? "visible translate-x-0" : "invisible -translate-x-full")
        }
      >
        <div className="flex items-center gap-2 px-4 pt-4">
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-faint)]" />
            <input
              ref={searchRef}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t("chapterSearch")}
              aria-label={t("chapterSearch")}
              tabIndex={open ? 0 : -1}
              className={
                "w-full rounded-lg bg-[var(--color-page)] py-2 pl-8 pr-3 text-sm coarse:min-h-11 " +
                "text-[var(--color-ink)] placeholder:text-[var(--color-faint)] focus:outline-none"
              }
            />
          </div>
          <IconButton label={t("chapterClose")} onClick={onClose} tabIndex={open ? 0 : -1}>
            <CloseIcon className="h-4 w-4" />
          </IconButton>
        </div>

        <div
          ref={list}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
        >
          {hits ? (
            hits.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-[var(--color-muted)]">
                {searching ? t("chapterSearching") : t("chapterNothingFound")}
              </p>
            ) : (
              hits.map((hit) => (
                <button
                  key={`${hit.chapter}-${hit.sentence}`}
                  tabIndex={open ? 0 : -1}
                  onClick={() => {
                    onGoToSentence(hit.sentence);
                    onClose();
                  }}
                  className="tap-row block w-full rounded-lg px-3 py-2 text-left transition-colors duration-150 hover:bg-[var(--color-raised)]"
                >
                  <span className="flex items-baseline justify-between gap-2 text-2xs text-[var(--color-faint)]">
                    <span>{t("readerChapter", { n: hit.chapter + 1 })}</span>
                    {hit.start !== null && <span className="font-mono">{clock(hit.start)}</span>}
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-[var(--color-muted)]">
                    <Snippet value={hit.snippet} />
                  </span>
                </button>
              ))
            )
          ) : (
            marks.map((mark) => {
              const page = pageAt(pagemap, mark.index, 0);
              const current = mark.index === chapterIndex;
              return (
                <button
                  key={mark.index}
                  data-chapter={mark.index}
                  tabIndex={open ? 0 : -1}
                  aria-current={current ? "true" : undefined}
                  onClick={() => {
                    onGoToChapter(mark.index);
                    onClose();
                  }}
                  className={
                    "tap-row flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left " +
                    "transition-colors duration-150 hover:bg-[var(--color-raised)] " +
                    (current ? "bg-[var(--color-accent-soft)]" : "")
                  }
                >
                  <span
                    className={
                      "w-8 shrink-0 font-mono text-xs tabular-nums " +
                      (current ? "text-[var(--color-accent)]" : "text-[var(--color-faint)]")
                    }
                  >
                    {mark.index + 1}
                  </span>
                  <span className="flex-1 truncate text-sm text-[var(--color-ink)]">
                    {t("readerChapter", { n: mark.index + 1 })}
                  </span>
                  <span className="shrink-0 font-mono text-2xs tabular-nums text-[var(--color-faint)]">
                    {page !== null && (
                      <span className="mr-2">{t("chapterPageShort", { page })}</span>
                    )}
                    {book.has_audio
                      ? clock(mark.start)
                      : t("chapterWordsShort", { count: Math.round(mark.chars / 6) })}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
}
